import Vue from 'vue'
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from 'vue-router'
import store from "@/store";

Vue.use(VueRouter)

/**
 * Deep link: ?context=<company_id> из getURLInContext() (уведомления, письма).
 * Переключает контекст компании, как выбор в CRM. Параметр затем убирается из URL.
 * Подмена id на чужую компанию → 404 от auth/set-context, данные чужой организации недоступны.
 */
async function applyCompanyContextFromQuery(to: Route, next: NavigationGuardNext): Promise<boolean> {
    const raw = to.query.context
    if (raw === undefined || raw === null || raw === '') {
        return false
    }

    const rawStr = Array.isArray(raw) ? raw[0] : raw
    const digits = String(rawStr ?? '').trim().match(/^(\d+)$/)
    const companyId = digits ? parseInt(digits[1], 10) : NaN

    const queryWithoutContext = { ...to.query }
    delete queryWithoutContext.context

    const stripAndNavigate = () => {
        next({
            path: to.path,
            query: queryWithoutContext,
            hash: to.hash,
            replace: true,
        })
    }

    if (!Number.isFinite(companyId) || companyId < 1) {
        stripAndNavigate()
        return true
    }

    const user = store.getters.user
    if (!user) {
        return false
    }

    if (user.company_context_id === companyId) {
        stripAndNavigate()
        return true
    }

    try {
        await store.dispatch('switchCompanyContext', companyId)
    } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response?.status
        if (status !== 404) {
            const errorHandler = (await import('@/services/errorHandler')).default
            errorHandler.handle(e as any)
        }
    }

    stripAndNavigate()
    return true
}

const routes: Array<RouteConfig> = [
    {
        path: '/',
        name: 'home',
        component: () => import('../views/HomeViewNew.vue'),
    },
    {
        path: '/dashboard-new',
        redirect: { name: 'home' },
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/Auth/LoginView.vue'),
        meta: {
            guest: true
        }
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('../views/Auth/RegisterView.vue'),
        meta: {
            guest: true
        }
    },
    {
        path: '/order/:slug/form',
        name: 'orderRequestForm',
        component: () => import('../views/OrderRequestForm.vue'),
        meta: {
            is_public: true,
        }
    },
    {
        path: '/privacy',
        name: 'privacy',
        component: () => import('../views/PrivacyPolicyView.vue'),
        meta: {
            is_public: true,
        }
    },
    {
        path: '/terms',
        name: 'terms',
        component: () => import('../views/TermsOfServiceView.vue'),
        meta: {
            is_public: true,
        }
    },
    {
        path: '/form',
        name: 'form',
        component: () => import('../views/Auth/FormView.vue'),
    },

    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('../views/Auth/ResetPasswordView.vue'),
        meta: {
            guest: true
        }
    },
    {
        path: '/reset-password-link',
        name: 'reset-password-link',
        component: () => import('../views/Auth/SendResetPasswordLinkView.vue'),
        meta: {
            guest: true
        }
    },
    {
        path: '/companies',
        name: 'companies',
        component: () => import('../views/Auth/CompaniesView.vue'),
    },
    {
        path: '/auth/branches',
        name: 'auth-branches',
        component: () => import('../views/Auth/BranchesView.vue'),
    },
    {
        path: '/users',
        name: 'users',
        component: () => import('../views/UsersView.vue'),
    },
    {
        path: '/security',
        name: 'security',
        component: () => import('../views/SecuritySessionsView.vue'),
        meta: { requiresCompanyAdmin: true },
    },
    {
        path: '/branches',
        name: 'branches',
        component: () => import('../views/BranchesView.vue'),
    },
    {
        path: '/customers',
        name: 'customers',
        component: () => import('../views/CustomersView.vue'),
    },
    {
        path: '/customers/import',
        name: 'customers-import',
        component: () => import('../views/CustomerImportView.vue'),
    },

    {
        path: '/customers/:id',
        name: 'customer',
        component: () => import('../views/CustomerItem.vue'),
    },

    {
        path: '/materials',
        name: 'materials',
        component: () => import('../views/MaterialsView.vue'),
    },
    {
        path: '/pricing',
        name: 'pricing',
        component: () => import('../views/PricingView.vue'),
    },
    {
        path: '/manufacturers',
        name: 'manufacturers',
        component: () => import('../views/ManufacturersView.vue'),
    },
    {
        path: '/stock',
        name: 'stock',
        component: () => import('../views/StockView.vue'),
    },
    {
        path: '/stock-goods',
        name: 'stock-goods',
        component: () => import('../views/StockGoodsView.vue'),
    },
  {
    path: '/stock-goods/inventories',
    name: 'goods-inventories',
    component: () => import('../views/GoodsInventoriesView.vue'),
  },
  {
    path: '/stock-goods/logs',
    name: 'goods-warehouse-logs',
    component: () => import('../views/GoodsWarehouseLogsView.vue'),
  },
  {
    path: '/stock-goods/shipments',
    name: 'goods-shipments',
    component: () => import('../views/GoodsShipmentsView.vue'),
  },
  {
    path: '/stock-goods/analytics',
    name: 'goods-analytics',
    component: () => import('../views/GoodsAnalyticsView.vue'),
  },
    {
      path: '/stock-goods/marketplace-orders',
      name: 'goods-marketplace-orders',
      component: () => import('../views/GoodsMarketplaceOrdersView.vue'),
    },
  {
    path: '/stock-goods/inventories/:id',
    name: 'goods-inventory-item',
    component: () => import('../views/GoodsInventoryItem.vue'),
  },
    {
        path: '/stock-goods/inventories/:id/logs',
        name: 'goods-inventory-logs',
        component: () => import('../views/GoodsInventoryLogsView.vue'),
    },
    {
        path: '/stock/mass-write-off',
        name: 'mass-write-off',
        component: () => import('../views/MassWriteOffView.vue'),
    },
    {
        path: '/inventories',
        name: 'inventories',
        component: () => import('../views/InventoriesView.vue'),
    },
    {
        path: '/inventories/:id',
        name: 'inventory',
        component: () => import('../views/InventoryItem.vue'),
    },
    {
        path: '/masks',
        name: 'masks',
        component: () => import('../views/ProdNumberMasks.vue'),
    },
    {
        path: '/accounts',
        name: 'accounts',
        component: () => import('../views/AccountsView.vue'),
    },
    {
        path: '/company-legal-entities',
        name: 'company-legal-entities',
        component: () => import('../views/CompanyLegalEntitiesView.vue'),
        meta: { requiresCompanyAdmin: true },
    },
    {
        path: '/cost-calculators/new',
        name: 'cost-calculator-template-new',
        component: () => import('../views/CostCalculatorTemplateEditView.vue'),
        meta: { requiresCompanyAdmin: true, requiresCostCalculatorModule: true },
    },
    {
        path: '/cost-calculators/:id/edit',
        name: 'cost-calculator-template-edit',
        component: () => import('../views/CostCalculatorTemplateEditView.vue'),
        meta: { requiresCompanyAdmin: true, requiresCostCalculatorModule: true },
    },
    {
        path: '/cost-calculators',
        name: 'cost-calculators',
        component: () => import('../views/CostCalculatorsView.vue'),
        meta: { requiresHigherThanCustomer: true, requiresCostCalculatorModule: true },
    },
    {
        path: '/requests',
        name: 'requests',
        component: () => import('../views/OrderRequestsView.vue'),
    },
    {
        path: '/order-requests/:id/email-history',
        name: 'order-request-email-history',
        component: () => import('../views/OrderRequestEmailHistoryView.vue'),
    },
    {
        path: '/novofon',
        name: 'novofon',
        component: () => import('../views/NovofonNotificationView.vue'),
    },
    {
        path: '/tochka-bank',
        name: 'tochka-bank',
        component: () => import('../views/TochkaBankView.vue'),
    },
    {
        path: '/ozon-bank',
        name: 'ozon-bank',
        component: () => import('../views/OzonBankView.vue'),
    },
    {
        path: '/orders',
        name: 'orders',
        component: () => import('../views/OrdersView.vue'),
    },
    {
        path: '/orders-kanban',
        name: 'orders-kanban',
        component: () => import('../views/OrdersKanbanView.vue'),
        meta: {
            requiresModerator: true
        }
    },
    {
        path: '/orders-gantt',
        name: 'orders-gantt',
        component: () => import('../views/OrdersGanttView.vue'),
        meta: {
            requiresModerator: true
        }
    },

    {
        path: '/orders/:id',
        name: 'order',
        component: () => import('../views/OrderItem.vue'),
    },
    {
        path: '/orders/manufactured/:id',
        name: 'orderManufactured',
        component: () => import('../views/OrderItemManufactured.vue'),
    },
    {
        path: '/deadline',
        name: 'orderDeadline',
        component: () => import('../views/OrdersDeadlineView.vue'),
    },
    {
        path: '/contracts',
        name: 'contracts',
        component: () => import('../views/ContractsView.vue'),
    },
    {
        path: '/contracts/templates',
        name: 'contractTemplates',
        component: () => import('../views/ContractTemplatesView.vue'),
    },
    {
        path: '/contracts/:id/tasks',
        name: 'contractTasks',
        component: () => import('../views/ContractTasksView.vue'),
    },
    {
        path: '/estimates',
        name: 'estimates',
        component: () => import('../views/EstimatesView.vue'),
    },
    {
        path: '/estimates/:id',
        name: 'estimate',
        component: () => import('../views/EstimateItem.vue'),
    },
    {
        path: '/search',
        name: 'search',
        component: () => import('../views/SearchView.vue'),
    },
    {
        path: '/notifications',
        name: 'notifications',
        component: () => import('../views/NotificationsView.vue'),
    },
    {
        path: '/money',
        name: 'money',
        component: () => import('../views/MoneyView.vue'),
    },
    {
        path: '/payment-purposes',
        name: 'payment-purposes',
        component: () => import('../views/PaymentPurposesView.vue'),
    },
    {
        path: '/regular-payments',
        name: 'regular-payments',
        component: () => import('../views/RegularPayments.vue'),
    },

    {
        path: '/year-summary',
        name: 'year-summary',
        component: () => import('../views/YearSummaryView.vue')
    },
    {
        path: '/newsletters',
        name: 'newsletters',
        component: () => import('../views/NewslettersView.vue')
    },
    {
        path: '/newsletters/create',
        name: 'newsletterCreate',
        component: () => import('../views/NewsletterEditor.vue')
    },
    {
        path: '/newsletters/:id',
        name: 'newsletter',
        component: () => import('../views/NewsletterItem.vue')
    },
    {
        path: '/newsletters/:id/edit',
        name: 'newsletterEdit',
        component: () => import('../views/NewsletterEditor.vue')
    },
    {
        path: '/order-notification-templates',
        name: 'order-templates',
        component: () => import('../views/OrderNotificationTemplates.vue')
    },
    {
        path: '/order-notification-templates/layout',
        name: 'orderNotificationEmailLayout',
        component: () => import('../views/OrderNotificationEmailLayoutView.vue')
    },
    {
        path: '/404',
        name: '404',
        component: () => import('../views/404.vue'),
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => import('../views/ProfileView.vue'),
    },
    {
        path: '/customer-home',
        name: 'customer-home',
        component: () => import('../views/CustomerHomeView.vue'),
        meta: { requiresCustomer: true },
    },
    {
        path: '/customer-orders',
        name: 'customer-orders',
        component: () => import('../views/CustomerOrdersView.vue'),
        meta: { requiresCustomer: true },
    },
    {
        path: '/customer-orders/:id',
        name: 'customer-order',
        component: () => import('../views/OrderItem.vue'),
        meta: { requiresCustomer: true },
    },
    {
        path: '/customer-documents',
        name: 'customer-documents',
        component: () => import('../views/CustomerDocumentsView.vue'),
        meta: { requiresCustomer: true },
    },
    {
        path: '/customer-payments',
        name: 'customer-payments',
        component: () => import('../views/CustomerPaymentsView.vue'),
        meta: { requiresCustomer: true },
    },
    {
        path: '/customer-profile',
        name: 'customer-profile',
        component: () => import('../views/ProfileView.vue'),
        meta: { requiresCustomer: true },
    },
    {
        path: '/customer-support',
        name: 'customer-support',
        component: () => import('../views/SupportView.vue'),
        meta: { requiresCustomer: true },
    },
    {
        path: '/tasks',
        name: 'tasks',
        component: () => import('../views/TasksView.vue'),
    },
    {
        path: '/tasks/:date',
        name: 'tasksDay',
        component: () => import('../views/TasksDayView.vue'),
    },
        {
            path: '/tasks-calendar',
            name: 'tasksCalendar',
            component: () => import('../views/TasksCalendarView.vue'),
        },
        {
            path: '/tasks-kanban',
            name: 'tasksKanban',
            component: () => import('../views/TasksKanbanView.vue'),
        },
    {
        path: '/task/:id',
        name: 'task',
        component: () => import('../views/TaskView.vue'),
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import('../views/Settings.vue'),
    },
    {
        path: '/delivery-addresses',
        name: 'delivery-addresses',
        component: () => import('../views/DeliveryAddressesView.vue'),
    },
    {
        path: '/subscription',
        name: 'subscription',
        component: () => import('../views/SubscriptionsView.vue'),
    },
    {
        path: '/store',
        name: 'store',
        component: () => import('../views/StoreView.vue'),
    },
    {
        path: '/lead-sources',
        name: 'leadSources',
        component: () => import('../views/LeadSourcesView.vue'),
    },
    {
      path: '/goods-marketplaces',
      name: 'goodsMarketplaces',
      component: () => import('../views/GoodsMarketplacesView.vue'),
    },
    {
      path: '/goods-categories',
      name: 'goodsCategories',
      component: () => import('../views/GoodsCategoriesView.vue'),
    },
    {
        path: '/printers',
        name: 'printers',
        component: () => import('../views/PrintersView.vue'),
    },
    // Legacy deep links from old printer notifications (/printers/:id)
    {
        path: '/printers/:id(\\d+)',
        redirect: (to) => ({ name: 'printer-detail', params: { id: to.params.id } }),
    },
    {
        path: '/print-queue',
        name: 'print-queue',
        component: () => import('../views/PrintQueueView.vue'),
        meta: { requiresPrinterMonitoringModule: true },
    },
    {
        path: '/monitoring',
        name: 'monitoring',
        component: () => import('../views/MonitoringView.vue'),
        meta: { requiresPrinterMonitoringModule: true },
    },
    {
        path: '/monitoring/:id',
        name: 'printer-detail',
        component: () => import('../views/PrinterDetailView.vue'),
        meta: { requiresPrinterMonitoringModule: true },
    },
    {
        path: '/support',
        name: 'support',
        component: () => import('../views/SupportView.vue'),
    },
    {
        path: '/notifications-test',
        name: 'notifications-test',
        component: () => import('../views/NotificationsTestView.vue'),
    },
    {
        path: '/funnel',
        name: 'funnel',
        component: () => import('../views/FunnelView.vue'),
    },
    {
        path: '/funnel/lead/:type/:id',
        name: 'funnel-lead',
        component: () => import('../views/FunnelLeadView.vue'),
    },
    {
        path: '/funnel/stages',
        name: 'funnel-stages',
        component: () => import('../views/FunnelStageView.vue'),
    },
    {
        path: '/funnel/create-lead',
        name: 'funnel-create-lead',
        component: () => import('../views/FunnelCreateLead.vue'),
    },
    {
        path: '/funnel/documentation',
        name: 'funnel-documentation',
        component: () => import('../views/FunnelDocumentation.vue'),
    },
    {
        path: '/funnel/admin/workflow-rules',
        name: 'funnel-workflow-rules',
        component: () => import('../views/FunnelWorkflowRulesView.vue'),
    },
    {
        path: '/funnel/reports',
        name: 'funnel-reports',
        component: () => import('../views/FunnelReportsView.vue'),
    },
    {
        path: '/funnel/notifications',
        name: 'funnel-notifications',
        component: () => import('../views/FunnelNotificationsView.vue'),
    },
    {
        path: '/funnel/:stage',
        name: 'funnel-stage',
        component: () => import('../views/FunnelStageView.vue'),
        props: true,
    },
    {
        path: '/platform-stats',
        name: 'platform-stats',
        component: () => import('../views/PlatformStatsView.vue'),
        meta: {
            requiresPlatformStats: true
        }
    },
    {
        path: '/usage-statistics',
        name: 'usage-statistics',
        component: () => import('../views/UsageStatisticsView.vue'),
        meta: {
            requiresPlatformStats: true
        }
    },
    {
        path: '/bot-management',
        name: 'bot-management',
        component: () => import('../views/BotManagementView.vue'),
        meta: {
            requiresAdminStats: true
        }
    },
    {
        path: '/activity-feed',
        name: 'activity-feed',
        component: () => import('../views/UserActivityFeedView.vue'),
        meta: {
            requiresHigherThanCustomer: true
        }
    },
    {
        path: '/reposts',
        name: 'reposts',
        component: () => import('../views/RepostsView.vue'),
        meta: {
            requiresCompanyAdmin: true
        }
    },
    {
        path: '/radio-versta',
        name: 'radio-versta',
        component: () => import('../views/RadioVerstaView.vue'),
        meta: {
            requiresCompanyAdmin: true
        }
    },
    {
        path: '/vk-callback',
        name: 'vk-callback',
        component: () => import('../views/VkCallbackView.vue'),
        meta: {
            requiresAuth: true
        }
    },

]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(to, from, savedPosition) {
        // Если есть сохраненная позиция (например, при нажатии кнопки "Назад")
        if (savedPosition) {
            return savedPosition;
        }
        // Если переход на якорь (hash)
        if (to.hash) {
            return {
                selector: to.hash,
                behavior: 'smooth'
            };
        }
        // Если изменились только query параметры (фильтры), сохраняем позицию
        if (to.path === from.path && to.name === from.name) {
            return; // Не скроллим, оставляем текущую позицию
        }
        // Во всех остальных случаях - прокрутка в начало страницы
        return { x: 0, y: 0 };
    }
})

router.beforeEach(async (to, from, next) => {
    if (to === from) {
        next(false);
        return;
    }

    if (to.matched.length === 0) {
        next({ path: '/404' })
    }

    const apiToken = !!store.getters.jwt;
    const guestRoute = to.matched.some(record => record.meta.guest);
    const isPublic = to.matched.some(record => record.meta.is_public);

    if (isPublic){
        next();
        return
    }

    if (guestRoute && !apiToken) {
        next();
    } else if (guestRoute && apiToken) {
        next({ path: "/" })
    } else if (!guestRoute && !apiToken) {
        next({ path: "/login" })
    } else {
        let user = store.getters.user;
        if (!user) {
            await store.dispatch('getAuthUser')
            user = store.getters.user;
        }

        if (await applyCompanyContextFromQuery(to, next)) {
            return
        }

        if (!user.company_context_id && !["form", "companies"].includes(String(to.name))) {
            next({ path: "/companies" })
            return
        }

        if (store.getters.needBranch && to.name !== 'auth-branches') {
            next({ path: "/auth/branches" })
            return
        }

        // Проверяем права доступа к роуту, требующему модератора
        const requiresModerator = to.matched.some(record => record.meta.requiresModerator);
        if (requiresModerator && !store.getters.isModerator) {
            next({ path: "/orders" }); // Редирект на обычную таблицу
            return;
        }

        // Проверяем права доступа к платформенной статистике
        const requiresPlatformStats = to.matched.some(record => record.meta.requiresPlatformStats);
        if (requiresPlatformStats && !user.is_admin_stats) {
            next({ path: "/" }); // Редирект на главную
            return;
        }

        // Проверяем права доступа к репостам (только админ компании)
        const requiresCompanyAdmin = to.matched.some(record => record.meta.requiresCompanyAdmin);
        if (requiresCompanyAdmin) {
            const company = store.getters.company;
            if (!company || company.access_level <= 1) {
                next({ path: "/" });
                return;
            }
            // Для роута /reposts дополнительно проверяем наличие модуля в подписке
            if (to.path === '/reposts') {
                const subscription = company?.subscription;
                if (!subscription || !subscription.modules) {
                    next({ path: "/" });
                    return;
                }
                const repostsModule = subscription.modules.find((m: any) => m.key === 'reposts');
                if (!repostsModule) {
                    next({ path: "/" });
                    return;
                }
            }
            if (to.path === '/radio-versta') {
                const subscription = company?.subscription;
                if (!subscription?.modules?.find((m: any) => m.key === 'radio_versta')) {
                    next({ path: "/" });
                    return;
                }
            }
        }

        // Проверяем права доступа к ленте событий (только для пользователей выше CUSTOMER)
        const requiresHigherThanCustomer = to.matched.some(record => record.meta.requiresHigherThanCustomer);
        if (requiresHigherThanCustomer) {
            const company = store.getters.company;
            // Проверяем, что уровень доступа выше CUSTOMER (0)
            // EMPLOYEE = 1, ADMIN = 2
            if (!company || company.access_level <= 0) {
                next({ path: "/" }); // Редирект на главную для клиентов и сотрудников
                return;
            }
        }

        const requiresCostCalculatorModule = to.matched.some(record => record.meta.requiresCostCalculatorModule);
        if (requiresCostCalculatorModule) {
            if (!store.getters.moduleEnabled('cost_calculator')) {
                next({ path: '/' });
                return;
            }
        }

        const requiresPrinterMonitoringModule = to.matched.some(record => record.meta.requiresPrinterMonitoringModule);
        if (requiresPrinterMonitoringModule) {
            if (!store.getters.moduleEnabled('printer_monitoring')) {
                next({ path: '/' });
                return;
            }
        }

        // Страницы клиентского портала — только для клиентов
        const requiresCustomer = to.matched.some(record => record.meta.requiresCustomer);
        if (requiresCustomer && !store.getters.isCustomer) {
            next({ path: "/" });
            return;
        }

        // Клиент — отдельная главная
        if (store.getters.isCustomer && to.name === 'home') {
            next({ path: '/customer-home' });
            return;
        }

        const route = store.getters.routes.find((route: any) => findRoute(route, to.path.split('/')[1]));

        // Новогодний роут всегда доступен (не требует permissions)
        if (route?.isNewYear) {
            next()
            return
        }

        if (!route?.key) {
            next()
        } else {
            let permission = store.getters.permissions[route.key]
            if (permission && permission["viewAny"]) {
                next()
            } else {
                next({ path: "/" });
            }
        }
    }
});

const findRoute = (route: any, needle: string) => {
    const routeKey = route.to?.split('/')?.[1];
    if (routeKey === needle) return route;
    if (route.children) {
        return route.children.find((child: any) => findRoute(child, needle))
    }
    return null;
}

export default router
